/**
 * 电子小票的链接构造与解析（纯函数，可单元测试）。
 *
 * ============================================================
 * 为什么要把这段单独抽出来测
 * ============================================================
 * 电子小票是「顾客扫码存到自己手机里」的那张凭据。它的链接有个很实际的坑：
 * 同一份前端产物要同时活在两个地址下 ——
 *
 *   https://order.jinhua.tech/order/     （主域名，子路径部署）
 *   https://api.wechgood.com/order/      （旧地址，308 跳到上面）
 *   浏览器直接 http://localhost:5174/     （本机开发）
 *
 * 路由用的是 hash 模式，所以链接必须是 `.../order/#/receipt?...` ——
 * 如果拼成 `.../order/receipt?...`（漏了 #），顾客扫出来就是 404 白屏。
 * 这种错误只有把链接拼出来看才能发现，所以写成纯函数 + 逐条测。
 *
 * 凭据说明：链接里带顾客自己的 openId（触屏机是「设备号+会话随机串」）。
 * 后端查单必须带凭据、且凭据只对得上自己那一单，所以链接被别人捡到也看不到别人的单；
 * 但它确实是这位顾客的凭据，**不要往公开地方发**。
 */

/** 小票页的路由名（hash 路由） */
export const RECEIPT_PATH = '/receipt';

/** 构造参数：用 location 的 origin + pathname，才能同时兼容上面三种部署形态 */
export interface ReceiptUrlInput {
  /** 一般是 window.location.origin，例如 https://order.jinhua.tech */
  origin: string;
  /** 一般是 window.location.pathname，例如 /order/ 或 / */
  pathname: string;
  orderNo: string;
  /** 顾客凭据（触屏机是自己生成的会话标识） */
  credential: string;
}

/** 把 base 路径整理成「以 / 开头、以 / 结尾」的形式 */
export function normalizeBasePath(pathname: string): string {
  const trimmed = (pathname || '/').split('#')[0].split('?')[0];
  const withLeading = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  return withLeading.endsWith('/') ? withLeading : `${withLeading}/`;
}

/**
 * 拼出这张电子小票的链接。
 *
 * ⚠️ 公式：`{origin}{base}#/receipt?o=单号&k=凭据`
 *    `#` 不能少（hash 路由），`o`/`k` 必须 encodeURIComponent。
 */
export function buildReceiptUrl(input: ReceiptUrlInput): string {
  const base = normalizeBasePath(input.pathname);
  const query = `o=${encodeURIComponent(input.orderNo)}&k=${encodeURIComponent(input.credential)}`;
  return `${input.origin.replace(/\/+$/, '')}${base}#${RECEIPT_PATH}?${query}`;
}

/** 从小票页的 URL 里把参数读回来 */
export function parseReceiptQuery(search: string): { orderNo: string; credential: string } | null {
  const raw = (search || '').replace(/^\?/, '');
  const params = new URLSearchParams(raw);
  const orderNo = params.get('o')?.trim() ?? '';
  const credential = params.get('k')?.trim() ?? '';
  if (orderNo === '' || credential === '') return null;
  return { orderNo, credential };
}
